import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";

import { mappingApi } from "@services/mappingApi";
import { triggerUrlDownload } from "@utils/jobResultsHelpers";

/**
 * Download the empty OE input workbook.
 *
 * The template is fetched, not bundled. Its four headers have to match what the
 * parser accepts character for character — a sheet spelled any other way is
 * refused outright — so the file is generated from the same constant the parser
 * reads. A copy shipped in this repo would go stale on the next rename and hand
 * the reviewer a file the backend rejects, with an error telling them to use
 * the template they just downloaded.
 *
 * The response is base64 rather than a URL because nothing is stored: the file
 * is identical for every caller and is built on the spot. Turning it into a
 * Blob URL here keeps the download flow the same as the result export's.
 */
export const useInputTemplate = (): {
  download: () => Promise<void>;
  isDownloading: boolean;
} => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    setIsDownloading(true);
    try {
      const template = await mappingApi.getOeInputTemplate();
      const url = URL.createObjectURL(template.blob);
      try {
        triggerUrlDownload(url, template.filename);
      } finally {
        // Revoking frees the blob; the click above has already handed the
        // bytes to the browser's download manager, so this does not race it.
        URL.revokeObjectURL(url);
      }
    } catch {
      notifications.show({
        color: "red",
        title: t("mappingServicePage.template.failedTitle", {
          defaultValue: "Could not download the template",
        }),
        message: t("mappingServicePage.template.failed", {
          defaultValue: "Please try again.",
        }),
      });
    } finally {
      setIsDownloading(false);
    }
  }, [t]);

  return { download, isDownloading };
};
